import { createApi } from '@reduxjs/toolkit/dist/query/react';
import type IBaseRequest from '../types/Base/BaseRequestInterface';
import {
  type TCardsResponse,
  type TApplyCardsSettingsRequest,
  type TCreateCardRequest,
  type TResetCardsIncomeRequest,
  type TToggleCardRequest,
  type TToggleCardsRequest,
  type TUpdateCardRequest,
} from '../types/CardsTypes';
import { baseQuery, getBaseQueryWithReauth } from 'redux/baseQueryAuth';

export const CardsApi = createApi({
  reducerPath: 'CardsApi',
  tagTypes: ['Cards'],
  baseQuery: getBaseQueryWithReauth(baseQuery),
  endpoints: (build) => ({
    getCards: build.query<TCardsResponse, IBaseRequest>({
      query: (params) => ({
        url: 'requisite/',
        params: {
          page: params?.page || 1,
          page_size: params?.page_size || 0,
          search: params?.search || undefined,
          active: params?.active,
        },
        method: 'GET',
      }),
      providesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    addCard: build.mutation<void, TCreateCardRequest | FormData>({
      query: (body) => ({
        url: 'requisite/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    toggleAllCards: build.mutation<void, TToggleCardsRequest>({
      query: (body) => ({
        url: 'requisite/toggle_all_requisites/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    toggleCard: build.mutation<void, TToggleCardRequest>({
      query: (params) => ({
        url: `requisite/${params.cardId}/`,
        method: 'PATCH',
        body: params.body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    editCard: build.mutation<void, TUpdateCardRequest>({
      query: (params) => ({
        url: `/requisite/${params.cardId}/`,
        method: 'PATCH',
        body: params.body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    resetCardIncome: build.mutation<void, number>({
      query: (cardId) => ({
        url: `/requisite/${cardId}/reset_income/`,
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    resetCardsIncome: build.mutation<void, TResetCardsIncomeRequest>({
      query: (body) => ({
        url: '/requisite/reset_income/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    applyCardsSettings: build.mutation<void, TApplyCardsSettingsRequest>({
      query: (body) => ({
        url: '/requisite/apply_settings/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    deleteCard: build.mutation<void, number>({
      query: (params) => ({
        url: `/requisite/${params}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
    getAllCards: build.query<TCardsResponse, IBaseRequest>({
      query: (params) => ({
        url: 'requisite/',
        method: 'GET',
        params: params?.page_size ? { page: 1, page_size: params.page_size } : undefined,
      }),
      providesTags: [{ type: 'Cards', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCardsQuery,
  useAddCardMutation,
  useToggleAllCardsMutation,
  useToggleCardMutation,
  useEditCardMutation,
  useResetCardIncomeMutation,
  useResetCardsIncomeMutation,
  useApplyCardsSettingsMutation,
  useDeleteCardMutation,
  useGetAllCardsQuery,
} = CardsApi;
